import * as ActionTypes from '../actions/actionTypes';
import {error} from '../actions/index';
import {geoserverBase} from '../config/environment';
import fuelTypes from '../config/fuel_types.json';
import {fetchJson} from '../utils/fetch_utils';
import {securityHeaders} from '../utils/security_utils';
import {pickFeatureValue} from '../utils/point_utils';
import {findInterval, utcToLocal} from '../utils/time_utils';
import {getNextVersion} from './asyncManagerMiddleware';

// Populates indicator data for a given point.
const _parseDataForExtendedData = function (layer, data, activeLayerPrefix) {
  const extendedData = [];
  const features = data.features;
  if (layer && (!activeLayerPrefix || layer.prefix !== activeLayerPrefix)) {
    if (features) {
      features.forEach(feature => {
        extendedData.push({
          indicator: layer,
          value: pickFeatureValue(layer, feature.properties)
        });
      });
    }
  }
  return extendedData;
};

const _parseDataForPrimaryData = function (layer, data, activeLayerPrefix) {
  let pointData = null;
  if (layer && layer.prefix === activeLayerPrefix) {
    const features = data.features;
    if (features && activeLayerPrefix) {
      features.forEach(feature => {
        pointData = {
          indicator: layer,
          value: pickFeatureValue(layer, feature.properties)
        };
      });
    }
  }
  return pointData;
};

// Each layer has a number of intervals for which there is data. Search through them looking for the one that contains
// the passed time, and return the time point of that interval. Returns null if no interval exists.
const _findStartOfInterval = function (layer, time) {
  if (!layer || !layer.available_times || !layer.available_times.intervals) {
    return null;
  }

  const interval = findInterval(layer.available_times.intervals, time);
  if (interval) {
    return interval.point_at;
  }
  return null;
};

const _constructRequestString = function (base, source, lat, lng, query_layers, time) {
  // Documentation for this request:
  // http://docs.geoserver.org/stable/en/user/services/wms/reference.html
  const baseRequestParameters = 'SERVICE=WMS&VERSION=1.1.1&REQUEST=GetFeatureInfo&SRS=EPSG:4326&X=1&Y=1&WIDTH=2&HEIGHT=2&INFO_FORMAT=application/json&EXCEPTIONS=application/json&FEATURE_COUNT=1';
  const formatted_time = new Date(time).toISOString();
  const bBoxSize = 0.02;

  return base + source + baseRequestParameters +
         '&QUERY_LAYERS=' + query_layers +
         '&LAYERS=' + query_layers +
         '&BBOX=' + (lng - bBoxSize) + ',' + (lat - bBoxSize) + ',' + (lng + bBoxSize) + ',' + (lat + bBoxSize) +
         '&TIME=' + formatted_time;
};

const _setPointFuelType = (value) => {
  const fuelTypeLookup = fuelTypes[value];
  const fuelType = fuelTypeLookup ? fuelTypeLookup.name : `Unknown (${value})`;
  const windReductioFactor = fuelTypeLookup ? fuelTypeLookup.wind_reduction_factor : 0;
  return {
    type: ActionTypes.SET_POINT_FUEL_TYPE,
    fuel_type: fuelType,
    wind_reduction_factor: windReductioFactor
  };
};

const _setPointTotalFuelLoad = (value) => {
  return {
    type: ActionTypes.SET_POINT_TOTAL_FUEL_LOAD,
    total_fuel_load: value
  };
};

const _populatePointDetails = (layer, data, activeLayerPrefix, asyncVersionPrimary, asyncVersionExtended) => {
  const primaryDetails = _parseDataForPrimaryData(layer, data, activeLayerPrefix);
  const extendedData = _parseDataForExtendedData(layer, data, activeLayerPrefix);
  return (dispatch) => {
    if (primaryDetails) {
      dispatch({
        type: ActionTypes.POPULATE_POINT_PRIMARY_DETAILS,
        asyncVersion: asyncVersionPrimary,
        details: primaryDetails
      });
    }
    if (extendedData.length > 0) {
      dispatch({
        type: ActionTypes.POPULATE_POINT_EXTENDED_DETAILS,
        asyncVersion: asyncVersionExtended,
        extended_data: extendedData
      });
    }
  };
};

const _populatePointDetailsAsync = (store, point, activeLayerPrefix, time) => {
  const dispatch = store.dispatch;
  const state = store.getState();

  // We are not looking at point details while dragging or playing.
  if (state.timeline_dnd.dragging || state.play.playing) {
    return;
  }

  const indicators = state.indicators.indicators;
  const app_mode = state.app.app_mode;

  const asyncVersionPrimary = getNextVersion(ActionTypes.POPULATE_POINT_PRIMARY_DETAILS);
  const asyncVersionExtended = getNextVersion(ActionTypes.POPULATE_POINT_EXTENDED_DETAILS);

  dispatch({ type: ActionTypes.CLEAR_POINT_DETAILS });
  if (point === null || time === undefined) {
    dispatch(_populatePointDetails(null, {}, activeLayerPrefix, asyncVersionPrimary, asyncVersionExtended));
  } else {
    // Look up the value for each indicator at the specified point and time.
    indicators.forEach(layer => {
      if (layer.available && !layer.hide_in_popup && (!layer.only_in_app_mode || layer.only_in_app_mode === app_mode)) {
        const base = layer.base || geoserverBase;
        const queryLayers = layer.params.layers;
        const layerTime = _findStartOfInterval(layer, time);
        if (layerTime) {
          const wmsTime = layer.local_time_for_wms ? utcToLocal(layerTime) : layerTime;
          fetchJson(_constructRequestString(base, layer.source, point.lat, point.lng, queryLayers, wmsTime),
            { headers: securityHeaders(layer.securityKey, state) })
          .then(data => {
            dispatch(_populatePointDetails(layer, data, activeLayerPrefix, asyncVersionPrimary, asyncVersionExtended));
            if (layer.prefix === 'FUEL_TYPE') {
              const primaryDetails = _parseDataForPrimaryData(layer, data, 'FUEL_TYPE');
              if (primaryDetails) {
                dispatch(_setPointFuelType(primaryDetails.value));
              }
            } else if (layer.prefix === 'FL_TOT') {
              const primaryDetails = _parseDataForPrimaryData(layer, data, 'FL_TOT');
              if (primaryDetails) {
                const fuelLoad = primaryDetails.value < 0 ? 0 : primaryDetails.value;
                dispatch(_setPointTotalFuelLoad(fuelLoad));
              }
            }
          })
          .catch(e => {
            dispatch(error(`${layer.displayName}: ${e.message}`));
          });
        }
      }
    });
  }
};

const _findNearestManualWeather = (store, latlng) => {
  store.dispatch({
    type: ActionTypes.FIND_NEAREST_MANUAL_WEATHER,
    latlng
  });
};

const _findNearestAws = (store, latlng) => {
  store.dispatch({
    type: ActionTypes.FIND_NEAREST_AWS,
    latlng
  });
};

const _findNearestFms = (store, latlng) => {
  store.dispatch({
    type: ActionTypes.FIND_NEAREST_FMS,
    latlng
  });
};

export const orchestratePointDetails = store => next => action => {
  // First complete the current action
  const result = next(action);

  const { point, indicators, time } = store.getState();
  const activeLayerPrefix = indicators.active ? indicators.active.prefix : null;

  switch (action.type) {
    case ActionTypes.SET_ACTIVE_POINT:
    case ActionTypes.SET_ACTIVE_TIME:
    case ActionTypes.SWITCH_ACTIVE_INDICATOR:
    case ActionTypes.POPULATE_INDICATOR_DETAILS:
    case ActionTypes.UPDATE_POINT_DETAILS:
    case ActionTypes.STOP_TIMELINE_DRAG:
      _populatePointDetailsAsync(store, point.latlng, activeLayerPrefix, time.selected_time);
      break;

    case ActionTypes.POPULATE_MANUAL_WEATHER_DATA:
      if (point.latlng) {
        _findNearestManualWeather(store, point.latlng);
      }
      break;

    case ActionTypes.POPULATE_AWS_DATA:
      if (point.latlng) {
        _findNearestAws(store, point.latlng);
      }
      break;

    case ActionTypes.POPULATE_FMS_DATA:
      if (point.latlng) {
        _findNearestFms(store, point.latlng);
      }
      break;

    default:
      break;
  }

  return result;
};



// WEBPACK FOOTER //
// ./src/middleware/pointDetailsMiddleware.js